// KANP-1897 — single named source of truth for "should the DCM AuthZ UI
// surface render right now?". Today there's exactly one consumer (the
// Data Contract Details page's Tabs scaffold + Access tab); any future
// DCM AuthZ surface should import this hook rather than re-deriving
// the gate from a permissions response.
//
// The gate flips off automatically when KANP-1850 lands and DCM is in
// ENFORCE mode (response's `enforce: bool` becomes true). At that point
// this hook becomes vestigial — to remove cleanly:
//   1. Delete this file + its sibling .test.ts
//   2. Delete the import in `admin/src/app/dataCatalog/details/page.tsx`
//   3. Delete the `useAuthzUiEnabled` call site + the
//      `requestedTab`/`activeTab` split (collapses back to one line)
//   4. Delete the `Tabs` render's `authzUiEnabled &&` conditional
//   5. In `page.test.tsx`: delete the `vi.mock` block for this hook,
//      the `mockReturnValue(true)` default in beforeEach, and the
//      `[AuthZ UI gate]` describe block

import { useMyContractPermissions } from './useDataContracts';

/**
 * Returns `true` when the DCM AuthZ UI should render for `contractId`.
 *
 * Source of truth: the per-contract permissions response's `enforce`
 * field (already returned for KANP-1474's Run-button gate + PII badge).
 * Reusing it here avoids a new boot-time round trip and keeps a single
 * `enforce` flow through the codebase.
 *
 * Defaults to `false` (gate closed) while the permissions request is
 * loading or has no data. We'd rather flash "no AuthZ UI" briefly than
 * flash AuthZ UI that vanishes on the first server response — the
 * latter would imply to users that they had access and then lost it.
 *
 * Same default-closed applies to error cases (404 on a deleted
 * contract, 5xx from DCM, network failure): the hook returns false and
 * the page silently falls back to the single-tab layout. Acceptable
 * because `useContract` runs in parallel and surfaces its own error
 * state for the user-visible failures we care about — the gate isn't
 * the right place to render auth/network errors.
 *
 * Returns `false` when `contractId` is null so callers can use the
 * hook unconditionally without an enabled-flag pattern.
 */
export function useAuthzUiEnabled( contractId: string | null ): boolean {
  const { data } = useMyContractPermissions( contractId );
  return data?.enforce ?? false;
}
